import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { BusinessTypeRequestsAbstractService } from 'bqx-businesstype-requests';
import { AbstractFlagrService } from 'bqx-feature-switches';
import { LoggerAbstractService, LogLevel } from 'bqx-logging';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { IAnswer, IApplication } from 'src/app/models/application-form/application-form.model';
import { Global } from 'src/app/models/shared/app-global.model';
import { ApplicationFormAbstractService } from 'src/app/services/application-form/application-form.abstract.service';
import { AbstractAuthService } from 'src/app/services/auth/auth.abstract.service';
import { SecondaryDataAbstractService } from 'src/app/services/secondary-data/secondary-data.abstract.service';

@Component({
  selector: 'appform-business-type-input',
  templateUrl: './business-type-questions.component.html',
  styleUrls: ['./business-type-questions.component.css']
})
export class BusinessTypeQuestionsComponent implements OnInit {
  @Input() read_only = false;
  @Input() readOnly: boolean;
  @Input() formData: IApplication;
  @Input() isAppSyncedWithBP$: BehaviorSubject<boolean>;
  @Input() isAppFormReady$ = new ReplaySubject<boolean>();
  @Output() webComponentLoad = new EventEmitter<boolean>();
  @Output() answerUpdate = new EventEmitter<IAnswer>();

  public answer: IAnswer;
  public showBecaToggle = false;

  public get data(): any {
    return this._clBecaData;
  }
  public get businessType(): string {
    return this.getBusinessTypeValue();
  }
  public get showPrimaryBusinessType$(): Observable<{ show: boolean; initial_value: string }> {
    return this._showPrimaryBusinessType$.asObservable();
  }
  public get getChecked(): boolean {
    return this._BECAchecked;
  }

  private static QUESTION_SET_CODE = 'progressive_commercial_business_type';
  private static QUESTION_CODE = 'progressive_commercial_naics';
  private _showPrimaryBusinessType$ = new ReplaySubject<{ show: boolean; initial_value: string }>();
  private _firstBecaLoad = false;
  private readonly _clBecaData = {
    access_token: this.authService.accessToken,
    request_data: {
      quote_number: Global.applicationId ? Global.applicationId : Global.sessionId,
      policy_number: '',
      product_version_number: '',
      clBECASwitch: true,
      clBECAVersion: '1',
      ssID: Global.sessionId
    },
    headerLabel: 'What provided service or services generate the most revenue for the business?.',
    headerSubLabel: '(Use a phrase for best results: "I make candles")',
    fallbackHeaderLabel: 'Business Type',
    fallbackSubHeaderLabel: null
  };
  // When BECA is initialized we output an event. We need this variable to buffer that change to allow the BECA toggle to work as we want
  private _initialBECAChangeBuffer = false;
  private _BECAchecked = true;

  constructor(
    private authService: AbstractAuthService,
    private businessTypeService: BusinessTypeRequestsAbstractService,
    private secondaryDataAbstractService: SecondaryDataAbstractService,
    private flagrService: AbstractFlagrService,
    private applicationFormService: ApplicationFormAbstractService,
    private loggerService: LoggerAbstractService
  ) { }

  ngOnInit(): void {
    const questionSet = this.formData.questionSets.list.find(
      (queSet) => queSet.question_set.code === BusinessTypeQuestionsComponent.QUESTION_SET_CODE
    );
    if (questionSet) {
      this.answer = questionSet.answers[0];
    }

    this.showBecaToggle = false;
  }

  public outputEventChange(event): void {
    if (event.type === 'ClearButtonChange') {
      this.setBusinessTypeAnswerValue(null, false);
    }
    if (event.type === 'BecaResultChange') {
      if (event?.data?.context?.conversation_id) {
        // This changes to true when BECA is initialized in the core code.
        this.showBecaToggle = this._initialBECAChangeBuffer;
        if (!this._initialBECAChangeBuffer) {
          this._initialBECAChangeBuffer = true;
        }
        this.secondaryDataAbstractService.secondaryData = {
          BECAInput: event.data.context.InitialSearchTerm,
          BECALabel: event.data.input?.text,
          SecondaryData: 'BECA',
          WatsonConversationId: event.data.context.conversation_id
        };

        if (event.data.context?.InitialSearchTerm && event.data.context?.guess_count <= 1) {
          this.loggerService.postLogData(LogLevel.Info, 'BECA Call', JSON.stringify(
            {
              success: true,
              searchTerm: event.data.context.InitialSearchTerm, 
              quoteNumber: this.applicationFormService.quoteNumber, 
              applicationId: sessionStorage.getItem('applicationId') ?? undefined,
              userName: JSON.parse(localStorage.getItem('client-user'))?.Username
            }));
        }
        if (!this._firstBecaLoad) {
          if (this.answer.value) {
            //data is available
            this._showPrimaryBusinessType$.next({ show: false, initial_value: this.answer.value });
            this._BECAchecked = false;
            this.showBecaToggle = true;
            this.setSecondaryData(this.answer.value);
          } else if (this.isAppSyncedWithBP$.getValue()) {
            this._showPrimaryBusinessType$.next({ show: !this.isAppSyncedWithBP$.getValue(), initial_value: this.getBusinessTypeValue() });
          }
          this._firstBecaLoad = true;
        }
      } else if (event.data?.error && event?.data?.fallBackMode) {
        this._BECAchecked = false;
        this.showBecaToggle = true;
        if (event.data?.initRetryCount > 0){
          this.setBusinessTypeAnswerValue(null, false);
        }
      } else {
        if (!event?.data?.context?.guess_count) {
          this.loggerService.postLogData(LogLevel.Info, 'BECA Call', JSON.stringify(
            {
              success: false,
              event: event,
              quoteNumber: this.applicationFormService.quoteNumber, 
              applicationId: sessionStorage.getItem('applicationId') ?? undefined,
              userName: JSON.parse(localStorage.getItem('client-user'))?.Username
            }));
        }
      }
      if (event.data?.output?.response_values === null && event.data?.input?.text){
        this.setBusinessTypeAnswerValue(null, false);
      }
      this.webComponentLoad.emit(true);
    }

    if (event.type === 'FinalClassificationChange') {
      if (event?.isFallback === true) {
        this.secondaryDataAbstractService.isFallback = true;
      } else {
        this.secondaryDataAbstractService.isFallback = false;
      }
      this.setSecondaryData(event.data.code);
      this.setBusinessTypeAnswerValue(event.data.code, true);
    }
  }

  private setBusinessTypeAnswerValue(value: string, isCompleted: boolean) {
    const answer = this.formData.questionSets.list
      .find((queSet) => queSet.question_set.code === BusinessTypeQuestionsComponent.QUESTION_SET_CODE)
      .answers.find((ans) => ans.question.code === BusinessTypeQuestionsComponent.QUESTION_CODE);

    const tempAns: IAnswer = { ...answer };

    const searchData = {
      apiUrl: AppConfig.settings.bqxCoreConfig.applicationFormApiUri,
      apiToken: localStorage.getItem('bp-sdk-token'),
      questioId: tempAns.question.id,
      searchPhrase: value
    };

    if (this.isAppSyncedWithBP$.getValue()) {
      if (value) {
        this.businessTypeService.businessTypeValidator.findBusinessType(searchData).subscribe((val) => {
          tempAns.choice_id = val?.body[0]?.id;
          this.setEmitTempAns(tempAns, isCompleted);
        });
      }
      else {
        tempAns.choice_id = null;
        this.setEmitTempAns(tempAns, isCompleted);
      }
    } else {
      tempAns.value = value;
      this.setEmitTempAns(tempAns, isCompleted);
    }
  }

  private setEmitTempAns(tempAns: IAnswer, isCompleted: boolean) {
    tempAns.is_completed = isCompleted;
    this.answerUpdate.emit(tempAns);
  }

  private setSecondaryData(val: string) {
    this.secondaryDataAbstractService.secondaryData.UserInsertDateTime = new Date();
    this.secondaryDataAbstractService.secondaryData.FinalWatsonIndexEntry = val;
  }

  public showBecaToggleChange(event: MatSlideToggleChange): void {
    this._showPrimaryBusinessType$.next({ show: event.checked, initial_value: null });
  }

  private getBusinessTypeValue(): string {
    const temp = this.formData.questionSets.list
      .filter((queSet) => queSet.question_set.code === BusinessTypeQuestionsComponent.QUESTION_SET_CODE)[0]
      .answers.filter((ans) => ans.question.code === BusinessTypeQuestionsComponent.QUESTION_CODE)[0];
    if (this.isAppSyncedWithBP$.getValue()) {
      if (this.secondaryDataAbstractService.secondaryData) {
        this.secondaryDataAbstractService.lastFinalClassification = temp.question.choices[0]?.value;
        this.secondaryDataAbstractService.secondaryData.FinalWatsonIndexEntry = temp.question.choices[0]?.value;
      }
      return temp.question.choices[0]?.value;
    } else {
      return temp.value;
    }
  }
}
